import { getChartType } from '@/explore/components/visualisation/utils';
import { isNumericField } from '@/explore/model/utils';
import {
  AggregatedVisualisation,
  ChartType,
  Field,
  HistogramVisualisation,
  InvalidVisualisation,
  SimpleVisualisation,
  ValidVisualisation,
  Visualisation,
  VisualisationOrientation,
  VisualisationViewOptions,
} from '@/explore/types';

export const isSimpleVisualisation = (
  visualisation: Visualisation,
): visualisation is SimpleVisualisation =>
  !isInvalidVisualisation(visualisation) &&
  !('aggregation' in visualisation) &&
  visualisation.mainAxisKey !== undefined;

export const isAggregatedVisualisation = (
  visualisation: Visualisation,
): visualisation is AggregatedVisualisation =>
  !isInvalidVisualisation(visualisation) && 'aggregation' in visualisation;

export const isHistogramVisualisation = (
  visualisation: Visualisation,
  fields: Field[],
): visualisation is HistogramVisualisation => {
  if (isInvalidVisualisation(visualisation)) {
    return false;
  }
  const mainAxisField = fields.find((field) => field.key === visualisation.mainAxisKey);
  return (
    mainAxisField !== undefined &&
    isNumericField(mainAxisField) &&
    isAggregatedVisualisation(visualisation) &&
    visualisation.aggregation.groups.length === 1 &&
    visualisation.aggregation.aggregations.length === 1 &&
    visualisation.aggregation.aggregations.at(0)?.type === 'count'
  );
};

export const isInvalidVisualisation = (
  visualisation: Visualisation,
): visualisation is InvalidVisualisation =>
  'type' in visualisation && visualisation.type === 'invalid';

export const unwrapInvalidVisualisation = (visualisation: Visualisation): ValidVisualisation =>
  isInvalidVisualisation(visualisation) ? visualisation.visualisation : visualisation;

export const setVisualisationOptions = (
  visualisation: ValidVisualisation,
  viewOptions: VisualisationViewOptions,
) => ({
  ...visualisation,
  viewOptions,
});

export const getVisualisationOptions = (
  visualisation: ValidVisualisation,
): VisualisationViewOptions => {
  return visualisation.viewOptions ?? {};
};

export const isVisualisationStacked = (visualisationOptions: VisualisationViewOptions): boolean =>
  visualisationOptions.stacked === true;

export const getVisualisationShowTotals = (
  visualisationOptions: VisualisationViewOptions,
): boolean => visualisationOptions.showTotals === true;

export const getVisualisationOrientation = (
  visualisation: ValidVisualisation,
  fields: Field[],
): VisualisationOrientation =>
  visualisation.viewOptions?.orientation ??
  getVisualisationDefaultOrientation(visualisation, fields);

const getVisualisationDefaultOrientation = (
  visualisation: ValidVisualisation,
  fields: Field[],
): VisualisationOrientation => {
  switch (getChartType(visualisation, fields)) {
    case 'bar':
      return 'horizontal';
    default:
      return 'vertical';
  }
};

export const getVisualisationDefaultChartType = (
  visualisation: ValidVisualisation,
  fields: Field[],
): ChartType => {
  switch (getChartType(visualisation, fields)) {
    case 'bar':
      return 'bar';
    default:
      return 'area';
  }
};
